import React, { useState, useEffect, useRef } from 'react';
import { Loader2, Sparkles, AlertCircle } from 'lucide-react';
import ReactMarkdown from 'react-markdown';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';
import { Button } from './ui/button';
import { Card } from './ui/card';

interface AIReviewDialogProps {
  isOpen: boolean;
  onClose: () => void;
  portfolioId: string;
}

const AIReviewDialog: React.FC<AIReviewDialogProps> = ({
  isOpen,
  onClose,
  portfolioId,
}) => {
  const [loading, setLoading] = useState(false);
  const [content, setContent] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [isComplete, setIsComplete] = useState(false);
  const contentRef = useRef<HTMLDivElement>(null);
  const abortControllerRef = useRef<AbortController | null>(null);

  const API_BASE_URL = process.env.REACT_APP_BACKEND_URL || '';

  useEffect(() => {
    if (isOpen && portfolioId) {
      startReview();
    }

    return () => {
      // Cleanup: abort any ongoing request when dialog closes
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, [isOpen, portfolioId]);

  // Auto-scroll to bottom as content streams in
  useEffect(() => {
    if (contentRef.current && !isComplete) {
      contentRef.current.scrollTop = contentRef.current.scrollHeight;
    }
  }, [content, isComplete]);

  const startReview = async () => {
    setLoading(true);
    setContent('');
    setError(null);
    setIsComplete(false);

    // Create abort controller for this request
    abortControllerRef.current = new AbortController();

    try {
      const response = await fetch(
        `${API_BASE_URL}/api/portfolios/${portfolioId}/ai-review/`,
        {
          method: 'POST',
          credentials: 'include',
          headers: {
            'Content-Type': 'application/json',
          },
          signal: abortControllerRef.current.signal,
        }
      );

      if (!response.ok) {
        // Clone response before consuming for error handling
        const errorText = await response.text();
        let errorMessage = 'Failed to start AI review';
        try {
          const errorData = JSON.parse(errorText);
          errorMessage = errorData.error?.message || errorData.detail || errorMessage;
        } catch {
          // If not JSON, use the text as error message
          if (errorText) errorMessage = errorText;
        }
        throw new Error(errorMessage);
      }

      // Handle streaming response
      const reader = response.body?.getReader();
      const decoder = new TextDecoder();

      if (!reader) {
        throw new Error('No response stream available');
      }

      setLoading(false);

      while (true) {
        const { done, value } = await reader.read();

        if (done) break;

        const chunk = decoder.decode(value);
        const lines = chunk.split('\n');

        for (const line of lines) {
          if (line.startsWith('data: ')) {
            try {
              const data = JSON.parse(line.slice(6));

              if (data.content) {
                setContent((prev) => prev + data.content);
              }

              if (data.done) {
                setIsComplete(true);
              }

              if (data.error) {
                setError(data.error);
                setIsComplete(true);
              }
            } catch (e) {
              // Skip malformed JSON
            }
          }
        }
      }
    } catch (err) {
      if (err instanceof Error && err.name === 'AbortError') {
        // Request was aborted, ignore
        return;
      }
      setError(err instanceof Error ? err.message : 'An error occurred');
      setLoading(false);
      setIsComplete(true);
    }
  };

  const handleClose = () => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    onClose();
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="max-w-4xl max-h-[85vh] flex flex-col">
        <DialogHeader className="flex-shrink-0">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-purple-100 rounded-lg">
              <Sparkles className="w-5 h-5 text-purple-600" />
            </div>
            <div>
              <DialogTitle className="text-xl">AI Portfolio Review</DialogTitle>
              <p className="text-sm text-gray-500 mt-1">
                Professional analysis of your portfolio's risk structure and discipline
              </p>
            </div>
          </div>
        </DialogHeader>

        <div className="flex-1 overflow-hidden mt-4">
          {loading ? (
            <div className="flex flex-col items-center justify-center h-64">
              <Loader2 className="w-10 h-10 animate-spin text-purple-600 mb-4" />
              <p className="text-gray-600 text-center">
                Analyzing your portfolio...
              </p>
              <p className="text-sm text-gray-400 mt-2">
                This may take 30-60 seconds
              </p>
            </div>
          ) : error ? (
            <Card className="p-6 bg-red-50 border-red-200">
              <div className="flex items-start gap-3">
                <AlertCircle className="w-5 h-5 text-red-600 mt-0.5" />
                <div>
                  <p className="font-medium text-red-800">Analysis Failed</p>
                  <p className="text-sm text-red-700 mt-1">{error}</p>
                  <Button
                    variant="outline"
                    size="sm"
                    className="mt-4"
                    onClick={startReview}
                  >
                    Try Again
                  </Button>
                </div>
              </div>
            </Card>
          ) : (
            <div
              ref={contentRef}
              className="h-[60vh] overflow-y-auto pr-4 scroll-smooth"
            >
              <div className="prose prose-sm max-w-none prose-headings:text-gray-900 prose-h1:text-xl prose-h1:font-bold prose-h1:mt-6 prose-h1:mb-3 prose-h2:text-lg prose-h2:font-bold prose-h2:mt-5 prose-h2:mb-2 prose-h3:text-base prose-h3:font-semibold prose-h3:mt-4 prose-h3:mb-2 prose-p:text-gray-700 prose-p:mb-2 prose-li:text-gray-700 prose-li:mb-1 prose-strong:text-gray-900 prose-ul:my-2 prose-ol:my-2">
                <ReactMarkdown>{content}</ReactMarkdown>
              </div>
              {!isComplete && content && (
                <div className="flex items-center gap-2 mt-4 text-purple-600">
                  <Loader2 className="w-4 h-4 animate-spin" />
                  <span className="text-sm">Analyzing...</span>
                </div>
              )}
            </div>
          )}
        </div>

        <div className="flex justify-end gap-3 pt-4 border-t mt-4 flex-shrink-0">
          <Button variant="outline" onClick={handleClose}>
            Close
          </Button>
          {isComplete && !error && (
            <Button
              variant="outline"
              className="gap-2"
              onClick={startReview}
            >
              <Sparkles className="w-4 h-4" />
              Re-analyze
            </Button>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default AIReviewDialog;
